// src/components/engine/EngineInfoBlock.jsx — Phase 1.1
//
// Директива v2.1 §3.1 + §3.2 + §3.3 (Phase 1.1):
//
// Три текстовых блока (НЕ фичи, только стратегический контекст):
//
// 1. "Why VIRAJ starts with paid traffic"
//    — объясняет ICP и validation environment
//
// 2. "Architecture designed to expand beyond arbitrage"
//    — показывает нейтральность архитектуры (semantic signals, not niche-specific)
//
// 3. "Potential Expansion Areas"
//    — стратегический вектор (текст только, НЕ roadmap, НЕ обещание, НЕ фичи)
//
// Does not implement ecommerce integrations, ML, or predictive systems.

export default function EngineInfoBlock({ compact = false }) {
  const mono = "var(--font-mono)";
  const text2 = "var(--text-secondary)";
  const muted = "var(--text-muted)";

  if (compact) {
    return (
      <div style={{
        padding: "10px 14px", borderRadius: "var(--radius)",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        fontFamily: mono, fontSize: 10, color: muted, lineHeight: 1.6,
      }}>
        VIRAJ is a decision & protection layer for unstable paid traffic operations.
        Designed to start with performance arbitrage, built to work across any high-velocity paid traffic environment.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* Block 1: Why paid traffic first */}
      <div style={{
        padding: "16px 18px", borderRadius: "var(--radius)",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
      }}>
        <div style={{
          fontFamily: mono, fontSize: 9, letterSpacing: "0.14em",
          textTransform: "uppercase", color: muted, marginBottom: 10,
        }}>
          Why VIRAJ starts with paid traffic
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {[
            "Performance arbitrage is the highest-velocity, highest-variance paid traffic environment.",
            "It stress-tests protection logic faster than any other channel — campaigns fail in hours, not weeks.",
            "Real operators in this environment make daily decisions under time pressure and incomplete attribution.",
            "This makes arbitrage the ideal validation layer for a decision & protection engine.",
            "Systems that survive here work anywhere."
          ].map((text, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "var(--accent-primary)", flexShrink: 0, marginTop: 1 }}>→</span>
              <span style={{ fontFamily: mono, fontSize: 11, color: text2, lineHeight: 1.55 }}>
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Block 2: Architecture neutrality */}
      <div style={{
        padding: "16px 18px", borderRadius: "var(--radius)",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
      }}>
        <div style={{
          fontFamily: mono, fontSize: 9, letterSpacing: "0.14em",
          textTransform: "uppercase", color: muted, marginBottom: 10,
        }}>
          Architecture designed for paid traffic operations
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {[
            "Signals are semantic — spend velocity, conversion degradation, attribution state — not channel-specific.",
            "The risk model operates on revenue-to-spend ratios and signal deviation, not arbitrage-specific metrics.",
            "Decision policies work on confidence levels and multi-signal confirmation — applicable to any traffic operation.",
            "The Decision Timeline captures incidents, not campaigns — making it reusable across any paid traffic context.",
            "Override Memory adapts to any operator behavior pattern, regardless of traffic source.",
          ].map((text, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "var(--success)", flexShrink: 0, marginTop: 1 }}>✓</span>
              <span style={{ fontFamily: mono, fontSize: 11, color: text2, lineHeight: 1.55 }}>
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Block 3: Potential expansion areas (text only — NOT a roadmap, NOT features) */}
      <div style={{
        padding: "16px 18px", borderRadius: "var(--radius)",
        background: "rgba(60,130,230,0.04)", border: "1px solid rgba(60,130,230,0.14)",
      }}>
        <div style={{
          fontFamily: mono, fontSize: 9, letterSpacing: "0.14em",
          textTransform: "uppercase", color: "var(--accent-primary)", marginBottom: 6, opacity: 0.7,
        }}>
          Potential expansion areas
        </div>
        <div style={{
          fontFamily: mono, fontSize: 10, color: muted, marginBottom: 10, lineHeight: 1.5,
        }}>
          These are signal categories the architecture can reason about — not planned features.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[
            "Conversion degradation detection",
            "Attribution integrity monitoring",
            "Traffic quality collapse signals",
            "Revenue delay anomaly detection",
            "Payment provider latency signals",
            "CAC instability patterns",
            "Checkout degradation signals",
          ].map((area, i) => (
            <div key={i} style={{
              padding: "4px 10px", borderRadius: "var(--radius-sm)",
              background: "rgba(60,130,230,0.06)", border: "1px solid rgba(60,130,230,0.14)",
              fontFamily: mono, fontSize: 10, color: "var(--text-secondary)",
            }}>
              {area}
            </div>
          ))}
        </div>
        <div style={{
          fontFamily: mono, fontSize: 9, color: muted, marginTop: 10, lineHeight: 1.5,
          borderTop: "1px solid var(--border-subtle)", paddingTop: 8,
        }}>
          Architecture is semantically neutral. Production expansion requires real traffic logs and replay analysis first.
        </div>
      </div>

    </div>
  );
}
